/**
 * Stage-events section for the Driver tab (mockup "Stage events").
 *
 * MVP scope: only what the existing `/api/events/stream`
 * SSE already carries — nothing invented, placeholder-empty otherwise:
 *
 *   - recording start/stop: derived from `device_telemetry.session_id`
 *     transitions (gate auto-arm/start and auto-stop both surface as
 *     exactly this flip; the backend sends session_id on every frame
 *     and null is authoritative).
 *   - bitrate changes: `command_update` frames whose command kind is
 *     raise_bitrate / lower_bitrate reaching `done`.
 *
 * Events accumulate client-side from page-open (no history endpoint in
 * MVP). Replay pages render the empty placeholder.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Activity, Play, Square } from 'lucide-react';

import { cn } from '@/lib/utils';
import api from '../../services/api';
import { FleetEvent } from '../../types';

export interface StageEventEntry {
  id: string;
  tsMs: number;
  kind: 'start' | 'stop' | 'bitrate';
  text: string;
  sub: string;
}

const MAX_ENTRIES = 20;

function bitrateLabel(payload: Record<string, unknown> | null | undefined): string | null {
  if (!payload) return null;
  for (const key of ['bitrate_bps', 'target_bps', 'video_bitrate_bps']) {
    const v = payload[key];
    if (typeof v === 'number' && Number.isFinite(v) && v > 0) {
      return `${(v / 1_000_000).toFixed(1)} Mbps`;
    }
  }
  return null;
}

export function useStageEvents(deviceId: string | null, live: boolean): StageEventEntry[] {
  const [entries, setEntries] = useState<StageEventEntry[]>([]);
  // undefined = no telemetry frame seen yet (seeds the baseline so the
  // first frame after page-open doesn't fabricate a "started" event).
  const lastSessionRef = useRef<string | null | undefined>(undefined);
  const seenCommandsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    setEntries([]);
    lastSessionRef.current = undefined;
    seenCommandsRef.current = new Set();
  }, [deviceId]);

  useEffect(() => {
    if (!live || !deviceId) return;
    const push = (entry: StageEventEntry) => {
      setEntries((prev) => [entry, ...prev].slice(0, MAX_ENTRIES));
    };
    const unsub = api.subscribeFleetEvents((evt: FleetEvent) => {
      if (evt.type === 'device_telemetry' && evt.device_id === deviceId) {
        const sid = evt.session_id ?? null;
        const prev = lastSessionRef.current;
        lastSessionRef.current = sid;
        if (prev === undefined || prev === sid) return;
        const now = Date.now();
        if (sid != null) {
          push({
            id: `start-${sid}-${now}`,
            tsMs: now,
            kind: 'start',
            text: 'Recording started',
            sub: `Gate auto-start or operator · session ${sid.slice(0, 18)}`,
          });
        } else {
          push({
            id: `stop-${prev}-${now}`,
            tsMs: now,
            kind: 'stop',
            text: 'Recording stopped',
            sub: 'Auto-stop or operator',
          });
        }
        return;
      }
      if (evt.type === 'command_update' && evt.command.device_id === deviceId) {
        const { kind, uuid, status, payload } = evt.command;
        if (kind !== 'raise_bitrate' && kind !== 'lower_bitrate') return;
        if (status !== 'done') return;
        if (seenCommandsRef.current.has(uuid)) return;
        seenCommandsRef.current.add(uuid);
        const target = bitrateLabel(payload);
        const now = Date.now();
        push({
          id: `bitrate-${uuid}`,
          tsMs: now,
          kind: 'bitrate',
          text:
            kind === 'raise_bitrate'
              ? `Uplink stepped up${target ? ` to ${target}` : ''}`
              : `Uplink stepped down${target ? ` to ${target}` : ''}`,
          sub: 'Auto bitrate adapt · store-and-forward catching up',
        });
      }
    });
    return () => unsub();
  }, [deviceId, live]);

  return entries;
}

const ICONS: Record<StageEventEntry['kind'], React.ReactNode> = {
  start: <Play className="size-3" />,
  stop: <Square className="size-3" />,
  bitrate: <Activity className="size-3" />,
};

// Arbitrary-value tints — the tailwind config maps `status.online` etc.
// to the solid token only; the *-bg tints live as raw CSS vars.
const ICON_TONE: Record<StageEventEntry['kind'], string> = {
  start: 'bg-[var(--status-online-bg)] text-status-online',
  stop: 'bg-[var(--status-error-bg)] text-status-error',
  bitrate: 'bg-[var(--status-warning-bg)] text-status-warning',
};

const fmtClock = (tsMs: number): string => {
  const d = new Date(tsMs);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  return `${hh}:${mm}:${ss}`;
};

export const StageEventsSection: React.FC<{ entries: StageEventEntry[] }> = ({
  entries,
}) => (
  <div className="border-b border-border-subtle p-3.5">
    <div className="mb-2.5 flex items-center justify-between text-[10px] font-bold uppercase tracking-wider text-fg-muted">
      <span>Stage events</span>
      <span className="font-medium normal-case tracking-normal">
        {entries.length === 0 ? '—' : `${entries.length} since page open`}
      </span>
    </div>
    {entries.length === 0 ? (
      <div className="py-1 font-mono text-[10px] text-fg-muted">
        no stage events yet
      </div>
    ) : (
      entries.map((e, i) => (
        <div
          key={e.id}
          className={cn(
            'flex gap-2.5 py-2',
            i < entries.length - 1 && 'border-b border-dashed border-border-subtle',
          )}
        >
          <div className="w-[58px] shrink-0 pt-0.5 font-mono text-[10px] text-fg-muted">
            {fmtClock(e.tsMs)}
          </div>
          <div
            className={cn(
              'flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full',
              ICON_TONE[e.kind],
            )}
          >
            {ICONS[e.kind]}
          </div>
          <div className="min-w-0 flex-1 text-[11px] leading-snug text-fg">
            {e.text}
            <div className="mt-px truncate text-[10px] text-fg-muted">{e.sub}</div>
          </div>
        </div>
      ))
    )}
  </div>
);

export default StageEventsSection;
